<template>
  <div class="goal-preview-stats">
    <div class="title">{{ goal_title }}</div>
    <div class="h-px bg-line mb-6"></div>
    <GoalPreviewStatsItem v-for="(goalStat,i) in goalStats" :key="i"
                          :class="goalStat.classes"
                          v-bind="goalStat"/>
  </div>
</template>

<script>
import GoalPreviewStatsItem from "@/components/cabinet/Money/Goals/GoalPreview/GoalPreviewStats/GoalPreviewStatsItem";
import {mapGetters} from "vuex";

export default {
  name: "GoalPreviewStats",
  components: {GoalPreviewStatsItem},
  props: {
    goal_type: {
      type: Number
    },
    goal_title: {
      type: String
    },
    account_title: {
      type: String
    },
    goal_sum: {
      type: Number
    },
    goal_date_start: {
      type: Number
    },
    goal_date_end: {
      type: Number
    },
    currency: {
      type: Object
    },
    summary: {
      type: Object,
    },
    status: {
      type: Number
    },
    updated_at: {
      type: Number
    }
  },
  computed: {
    ...mapGetters('cabinet/currencies', ['displayCurrencyValue']),
    goalStats() {
      let moneyInMonths = (money, months) => {
        months = Math.abs(months);
        let amount = this.displayCurrencyValue(money, this.currency, true, true)
        if (months >= 12) {
          let years = Math.floor(months / 12),
              restMonths = months % 12;
          return restMonths
              ? `${amount} ${this.$t('in')} ${this.$tc('years', years)} ${this.$tc('months', restMonths)}`
              : `${amount} ${this.$t('in')} ${this.$tc('years', years)}`;
        }
        return months ? `${amount} ${this.$t('in')} ${this.$tc('months', months)}` : `${amount}`;
      }
      let mainTextColor = this.summary.color === 'gray' ? 'border-hover' : this.summary.color,
          stats = [],
          statsClasses = [
            'mb-4',
            'mb-4',
            'mb-16',
            'mb-10 font-bold big',
            `mb-4 text-${mainTextColor}`,
            'mb-4',
          ];

      if (this.goal_type === 1) {
        stats = [
          {label: this.$t('start'), value: this.$moment.unix(this.goal_date_start).format('MMMM YYYY')},
          {label: this.$t('finish'), value: this.$moment.unix(this.goal_date_end).format('MMMM YYYY')},
          {label: this.$t('account'), value: this.account_title},
          {label: this.$t('goal'), value: this.displayCurrencyValue(this.goal_sum, this.currency, true, true),},
          {
            label: this.$t('currentlyHave'),
            value: moneyInMonths(this.summary.payment_made, this.summary.months_passed)
          },
          {
            label: this.$t('left'),
            value: moneyInMonths(this.summary.payment_left, this.summary.months_left)
          },
          {
            label: this.$t('monthlyPayment'),
            value: this.displayCurrencyValue(this.summary.payment_plan, this.currency, true, true)
          },
        ]
      } else if (this.goal_type === 2) {
        stats = [
          {label: this.$t('start'), value: this.$moment.unix(this.goal_date_start).format('MMMM YYYY')},
          {label: this.$t('finish'), value: this.$moment.unix(this.goal_date_end).format('MMMM YYYY')},
          {label: this.$t('account'), value: this.account_title},
          {
            label: this.$t('leftPayOff'),
            value: moneyInMonths(this.summary.payment_left, this.summary.months_left)
          },
          {label: this.$t('entered'), value: moneyInMonths(this.summary.payment_made, this.summary.months_passed)},
          {label: this.$t('creditSum'), value: this.displayCurrencyValue(this.goal_sum, this.currency, true, true),},
          {
            label: this.$t('monthlyPayment'),
            value: this.displayCurrencyValue(this.summary.payment_plan, this.currency, true, true)
          }
        ]
      }

      if (this.status === 2) {
        statsClasses.splice(4, 0, 'mb-10 font-bold big');
        statsClasses[3] = 'mb-4 font-bold big';
        stats.splice(4, 0, {
          label: this.$t('completed'),
          value: this.$moment.unix(this.updated_at).format('DD MMMM YYYY')
        });
        stats[7].label = this.$t('averageMonthlyPayment');
        stats[7].value = this.displayCurrencyValue(this.summary.payment_avg, this.currency, true, true);

        if (this.goal_type === 1) {
          stats[5].value = moneyInMonths(this.summary.payment_made, this.summary.months_passed);
          stats[6].label = this.$t('leftToSave');
          stats[6].value = moneyInMonths(this.summary.payment_left, 0);
        } else {
          stats[3].label = this.$t('leftRepaid');
          stats[3].value = moneyInMonths(this.summary.payment_left, 0)
          stats[5].value = moneyInMonths(this.summary.payment_made, this.summary.months_passed);
          stats[6].value = moneyInMonths(this.goal_sum, 0);
        }
      }
      return stats.map((item, index) => ({...item, classes: statsClasses[index]}))
    }
  },
  i18n: {
    messages: {
      en: {
        account: 'Account',
        start: 'Start',
        finish: 'Finish',
        goal: 'Goal',
        currentlyHave: 'Accumulated',
        completed: 'Completed',
        left: 'Remaining',
        leftRepaid: 'To be repaid',
        leftToSave: 'Left to save',
        monthlyPayment: 'Monthly payment',
        averageMonthlyPayment: 'Average monthly payment',
        leftPayOff: 'Left to pay off',
        payedOff: 'Paid off',
        entered: 'Paid off',
        creditSum: 'Credit amount',
        in: 'in',
        months: '{n} month | {n} months',
        years: '{n} year | {n} years',
      },
      ru: {
        account: 'Счет',
        start: 'Старт',
        finish: 'Финиш',
        goal: 'Цель',
        currentlyHave: 'Накоплено',
        completed: 'Завершена',
        left: 'Осталось',
        leftRepaid: 'Оставалось погасить',
        leftToSave: 'Оставалось накопить',
        monthlyPayment: 'Ежемесячный платеж',
        averageMonthlyPayment: 'Средний ежемесячный платеж',
        leftPayOff: 'Осталось погасить',
        payedOff: 'Погашено',
        entered: 'Внесено',
        creditSum: 'Сумма кредита',
        in: 'за',
        months: '0 месяцев | {n} месяц | {n} месяца | {n} месяцев',
        years: '0 лет | {n} год | {n} года | {n} лет'
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.goal-preview-stats {
  @apply text-text;
  .title {
    @apply font-bold mb-4 mr-12 truncate;
    font-size: 36px;
  }
}
</style>